import clsx from 'clsx'
import React, { type ButtonHTMLAttributes, type ReactNode } from 'react'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  fullWidth?: boolean
  gradient?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      loading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      gradient = false,
      className,
      disabled,
      ...props
    },
    ref,
  ) => {
    const baseStyles =
      'relative inline-flex items-center justify-center font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed'

    const variants = {
      primary: gradient
        ? 'bg-gradient-warm text-white hover:shadow-glow hover:scale-105 focus:ring-primary-500'
        : 'bg-primary-500 text-white hover:bg-primary-600 hover:shadow-medium hover:scale-105 focus:ring-primary-500',
      secondary:
        'bg-secondary-500 text-white hover:bg-secondary-600 hover:shadow-medium hover:scale-105 focus:ring-secondary-500',
      outline:
        'border-2 border-primary-500 text-primary-600 bg-white hover:bg-primary-50 hover:scale-105 focus:ring-primary-500',
      ghost: 'text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900 focus:ring-neutral-400',
      danger: 'bg-red-500 text-white hover:bg-red-600 hover:shadow-medium hover:scale-105 focus:ring-red-500',
    }

    const sizes = {
      sm: 'px-4 py-2 text-sm rounded-xl gap-1.5',
      md: 'px-6 py-3 text-base rounded-2xl gap-2',
      lg: 'px-8 py-4 text-lg rounded-3xl gap-2.5',
    }

    const widthStyles = fullWidth ? 'w-full' : ''

    const spinnerSizes = {
      sm: 'h-3.5 w-3.5',
      md: 'h-4 w-4',
      lg: 'h-5 w-5',
    }

    return (
      <button
        ref={ref}
        className={clsx(baseStyles, variants[variant], sizes[size], widthStyles, loading && 'cursor-wait', className)}
        disabled={disabled || loading}
        {...props}
      >
        {/* Background blur effect on hover */}
        <span className="absolute inset-0 rounded-inherit opacity-0 hover:opacity-100 transition-opacity duration-300 bg-gradient-to-r from-primary-500/10 to-secondary-500/10 blur-xl" />

        <span className="relative flex items-center justify-center">
          {loading ? (
            <>
              <svg className={clsx('animate-spin', spinnerSizes[size])} viewBox="0 0 24 24" aria-hidden="true">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="ml-2">처리 중...</span>
            </>
          ) : (
            <>
              {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
              {children}
              {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
            </>
          )}
        </span>
      </button>
    )
  },
)

Button.displayName = 'Button'

export default Button
